import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Animated from 'react-native-reanimated';
import { getSyncingStatus, setRetainedConnections } from 'actions';
import { AppView, Button, rem, SCROLL_INDICATOR_INSETS, Spacer, withAnimatedNavigationHeader } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import { tierThemes } from 'features/premium/benefits';
import { Tier } from 'features/premium/entities';
import { selectConnectionsLimit } from 'features/premium/selectors';
import { muteErrorAndResult, useFetchOld } from 'hooks/useFetch';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectOrderedConnections, selectSavingsPlanConnectionId } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { isUnlocked } from 'utils';
import RadioButton from 'design-system/RadioButton';
import { selectUserTier } from 'features/premium/selectors/tier';
import { SET_UPGRADED_IN_ACTIVATE_FUNNEL, SET_SELECTED_CONNECTIONS_BEFORE_UPGRADED_IN_LOGINS_UPGRADE_FUNNEL, } from 'actions/types';
import { selectFeaturesUsedWhenOnPremium } from 'reducers/selectors/premium';
import useNavigateToMissedFeaturesAfterDowngradeToBasic from 'features/premium/hooks/useNavigateToMissedFeaturesAfterDowngradeToBasic';
import BankLoginCard from './BankLoginCard';
import BankNameRow from './BankNameRow';
import LoginsUpgradeHelpButton from './LoginsUpgradeHelpButton';
import { allowedConnectionsInfo, getRequiredTier } from './utils';
import ConnectionUsedInAutosaveModal from './components/ConnectionUsedInAutosaveModal';
const keyExtractor = (item) => item.id.toString();
const LoginsUpgradeScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <LoginsUpgradeHelpButton black/>,
        });
    }, []);
    const fromConnectionId = route.params?.fromConnectionId;
    const downgradeFlowIndex = route.params?.downgradeFlowIndex;
    const features = useAppSelector(selectFeaturesUsedWhenOnPremium);
    const featuresLength = features?.length;
    const connections = useAppSelector((store) => selectOrderedConnections(store, fromConnectionId));
    const savingsPlanConnectionId = useAppSelector(selectSavingsPlanConnectionId);
    const connectionsLimitMap = useAppSelector(selectConnectionsLimit);
    const userTier = useAppSelector(selectUserTier);
    const { navigateToNext } = useNavigateToMissedFeaturesAfterDowngradeToBasic();
    const hasIntroScreen = useMemo(() => !!navigation.getState().routes.find((r) => r.name === 'LoginsUpgradeIntro'), []);
    const [selected, setSelected] = useState(connections.reduce((prev, curr) => ({
        ...prev,
        [curr.id]: curr.id === savingsPlanConnectionId ||
            (!hasIntroScreen &&
                !downgradeFlowIndex &&
                (curr.deactivatedOverQuota === false || curr.id === fromConnectionId)),
    }), {}));
    const navigateToAutosave = useCallback(() => {
        navigation.navigate('AutosaveSelectBank');
    }, []);
    const renderItem = useCallback(({ item, index }) => (<>
          <TouchableBounce onPress={() => {
            if (savingsPlanConnectionId === item.id) {
                ConnectionUsedInAutosaveModal.show(navigateToAutosave, 'deactivate');
                return;
            }
            setSelected((state) => ({ ...state, [item.id]: !selected[item.id] }));
        }}>
            <BankLoginCard headerRow={<View style={styles.titleRow}>
                  {savingsPlanConnectionId === item.id ? (<RadioButton style={styles.checkBox} isChecked type="multiple"/>) : (<RadioButton type="multiple" style={styles.checkBox} isChecked={selected[item.id] || false} testID={`checkbox.${item.id}`}/>)}
                  <BankNameRow data={item.bankInfo}/>
                </View>} data={item} hideLinkedFor hideButtons hideStatusMessage savingsPlanConnectionId={savingsPlanConnectionId}/>
          </TouchableBounce>
          {index !== connections.length - 1 && <Spacer h={16}/>}
        </>), [connections.length, navigateToAutosave, savingsPlanConnectionId, selected]);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const dispatch = useAppDispatch();
    const isEmpty = !connections || connections.length === 0;
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(32));
    const requiredTier = useMemo(() => {
        const totalSelected = Object.values(selected).reduce((prev, curr) => (curr ? prev + 1 : prev), 0);
        return getRequiredTier(connectionsLimitMap, totalSelected);
    }, [connectionsLimitMap, selected]);
    const saveRetained = useCallback(async () => {
        const selectedIds = Object.keys(selected).filter((key) => selected[key] === true);
        await fetch(setRetainedConnections(selectedIds.map((id) => Number(id))));
        dispatch(getSyncingStatus());
    }, [selected]);
    const needsUpgrade = requiredTier !== Tier.free && !isUnlocked(userTier, requiredTier);
    const onPressContinue = useCallback(async () => {
        if (needsUpgrade) {
            navigation.navigate('EmmaProModal', {
                tier: requiredTier,
                isTierRequired: true,
                upsellTrial: false,
                onUpgradeCallback: () => {
                    if (downgradeFlowIndex) {
                        saveRetained();
                    }
                    if (hasIntroScreen) {
                        dispatch({
                            type: SET_SELECTED_CONNECTIONS_BEFORE_UPGRADED_IN_LOGINS_UPGRADE_FUNNEL,
                            payload: selected,
                        });
                    }
                    else {
                        saveRetained();
                        dispatch({
                            type: SET_UPGRADED_IN_ACTIVATE_FUNNEL,
                            payload: true,
                        });
                    }
                    navigation.pop(hasIntroScreen ? 2 : 1);
                },
            });
        }
        else {
            await saveRetained();
            if (downgradeFlowIndex) {
                navigateToNext(downgradeFlowIndex);
            }
            else {
                navigation.pop(hasIntroScreen ? 2 : 1);
            }
        }
    }, [downgradeFlowIndex, hasIntroScreen, needsUpgrade, requiredTier, saveRetained, selected, navigateToNext]);
    const renderHeader = useMemo(() => {
        const title = downgradeFlowIndex
            ? 'Select the bank connections you want to keep'
            : `Select the bank connections you want to ${hasIntroScreen ? 'keep' : 'activate'}`;
        const subTitle = downgradeFlowIndex
            ? `You can keep ${connectionsLimitMap.FREE} on Basic, ${connectionsLimitMap.PLUS} on Emma Plus and unlimited on Pro or Ultimate`
            : `${allowedConnectionsInfo(connectionsLimitMap)} ${hasIntroScreen
                ? 'Connections you don’t select will become inactive.'
                : 'Connections you don’t select will stay inactive.'}`;
        return (<ScreenInfoHeader title={title} subTitle={subTitle} preTitle={downgradeFlowIndex ? `Review features ${downgradeFlowIndex}/${featuresLength}` : undefined}/>);
    }, [connectionsLimitMap, downgradeFlowIndex, featuresLength, hasIntroScreen]);
    const data = useMemo(() => {
        if (hasIntroScreen || downgradeFlowIndex) {
            return connections;
        }
        return connections.filter((conn) => conn.deactivatedOverQuota);
    }, [connections, downgradeFlowIndex, hasIntroScreen]);
    const hasSelected = useMemo(() => data.filter((account) => selected[account.id]).length > 0, [data, selected]);
    return (<AppView>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.FlatList onScroll={scrollHandler} contentContainerStyle={paddingBottom} ListHeaderComponent={renderHeader} initialNumToRender={6} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} data={data} keyExtractor={keyExtractor} renderItem={renderItem}/>
        {!isEmpty && (<Button brand floating title={needsUpgrade ? `Upgrade to ${tierThemes[requiredTier].shortName}` : 'Continue'} onPress={onPressContinue} isFetching={isFetching} preventDoubleTap disabled={!hasSelected} testID="continueButton"/>)}
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    titleRow: {
        flexDirection: 'row',
        padding: rem(16),
        alignItems: 'center',
    },
    checkBox: {
        marginRight: rem(16),
    },
});
export default LoginsUpgradeScreen;
